import type { QuestionnaireResponseItemAnswer } from 'fhir/r4';
import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import type { DropdownProps } from 'semantic-ui-react';
import { Input, Form, TextArea, Dropdown } from 'semantic-ui-react';
import type { Option } from 'types';

import AuthContext from '../../../../contexts/AuthContext';
import * as FHIRUtils from '../../../../utils/FHIRUtils';
import { CONSULT_TOPIC_OPTIONS } from '../../careplan/tasks/taskDetails/fixtures';
import { ADDENDUM_FIELD_IDS, ALL_COSIGN_PRACTITIONER_IDS } from '../constants';
import { getNpiPractitioners } from '../fhir-helpers';

import type { QuestionnaireStringProps } from './item-props';

/** A prior completed document-upload survey, offered for addendum selection. */
type CompletedSurveyOption = Option & {
  completedDate: string;
};

/**
 * SUSPECTED DEFECT — the six `<Dropdown value={selected*}>` below are handed
 * state the effects deliberately set to `null` to mean "no answer", but Semantic's
 * `DropdownProps['value']` admits no `null`. React reads `value={null}` as
 * *uncontrolled*, so the dropdown keeps a stale selection after the answer is
 * cleared. Passing `undefined` is the one-character fix, but it changes what React
 * does with these controls, so it needs someone who can confirm the intended
 * clear-behaviour. This forwards the value untouched.
 */
const asDropdownValue = (value: string | null | undefined): NonNullable<DropdownProps['value']> =>
  value as NonNullable<DropdownProps['value']>;

export default function String(props: QuestionnaireStringProps) {
  const { practitionerName } = useContext(AuthContext) ?? {};
  //props: addAnswer, i, j, currentAnswer, enable
  const [selectedReferralId, setSelectedReferralId] = useState<string | null | undefined>(null);
  const [activeReferralList, setActiveReferralList] = useState<Option[]>([]);
  const [selectedServiceProvider, setSelectedServiceProvider] = useState<string | null | undefined>(null);
  const [rdHcPractitionerList, setRdHcPractitionerList] = useState<Option[]>([]); //show RD and Health Coach dropdown
  const [selectedConsultTask, setSelectedConsultTask] = useState<string | null | undefined>(null);
  const [completedDumentSurveyList, setCompletedDumentSurveyList] = useState<CompletedSurveyOption[]>([]); //show completed document upload survey dropdown
  const [selectedCompletedDumentSurvey, setSelectedCompletedDumentSurvey] = useState<string | null | undefined>(null);
  const [cosignPractitionerList, setCosignPractitionerList] = useState<Option[]>([]);
  const [selectedCosignPractitioner, setSelectedCosignPractitioner] = useState<string | null | undefined>(null);
  const [selectedGroupConsultTopic, setSelectedGroupConsultTopic] = useState<string | null | undefined>(null);

  useEffect(() => {
    if (props.practitionerData) {
      const practitionersList: Option[] = [];
      props.practitionerData.map((item) => {
        const practitioner = FHIRUtils.parseFHIRPractitioner(item.resource!);
        //only show RD and HC in the service provider dropdown
        if (['RD', 'HC'].includes(practitioner.type)) {
          practitionersList.push({
            key: practitioner.id,
            text: practitioner.fullName,
            value: practitioner.id,
          });
        }
      });
      setRdHcPractitionerList(practitionersList);
    }
  }, [props.practitionerData]);

  useEffect(() => {
    if (props.practitionerData) {
      const npiList = getNpiPractitioners(props.practitionerData)
        .filter((p) => !props.excludePractitionerId || p.id !== props.excludePractitionerId)
        .map((p) => ({
          key: p.id,
          text: p.fullName,
          value: p.id,
        }));
      setCosignPractitionerList(npiList);
    }
  }, [props.practitionerData, props.excludePractitionerId]);

  /**
   * Recomputed every render from bundle entries so the consult dropdown updates when any Task changes
   * (e.g. `meta.lastUpdated` after a care plan save), even if the `tasksData` object reference is unchanged.
   */
  const taskBundleSyncKey =
    props?.tasksData?.entry
      ?.map((e) => {
        const r = e?.resource;
        return `${r?.id ?? ''}:${r?.meta?.lastUpdated ?? ''}:${r?.status ?? ''}`;
      })
      .join('|') ?? '';

  const consultTaskList = useMemo(() => {
    if (!props?.tasksData?.entry?.length) {
      return [];
    }
    const consultTasks: Array<{ key: string; text: string; value: string; duedate: string }> = [];
    for (const item of props.tasksData.entry) {
      if (!item?.resource) continue;
      const task = FHIRUtils.parseFHIRTask(item.resource);
      if (task.code === 'consultation' && task.status?.toLowerCase() !== 'cancelled') {
        consultTasks.push({
          key: task.id,
          text: `${task.reasonCode}-${task.dueDate}`,
          value: task.id,
          duedate: task.dueDate,
        });
      }
    }
    consultTasks.sort((a, b) => a.duedate.localeCompare(b.duedate));
    return consultTasks;
  }, [props?.tasksData, taskBundleSyncKey]);

  useEffect(() => {
    if (props?.questionnaireResponseData) {
      //filter and update document survey list as dropdown
      const documentSurveyList: CompletedSurveyOption[] = [];
      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      props?.questionnaireResponseData?.entry?.length !== undefined &&
        props.questionnaireResponseData.entry.length > 0 &&
        props?.questionnaireResponseData?.entry?.map((item) => {
          const currentQR = FHIRUtils.parseQuestionnaireResponse(item.resource);
          if (
            currentQR?.questionnaireId === 'member-program-document-upload-pw' &&
            currentQR?.status === 'completed' &&
            currentQR.id !== props?.currentQrId
          ) {
            documentSurveyList.push({
              key: currentQR.id,
              text: `completed by ${currentQR.lastModifiedBy} on ${moment(currentQR.completedDate).format('MM/DD/YYYY')}`,
              value: currentQR.id,
              completedDate: moment(currentQR.completedDate).format('MM/DD/YYYY'),
            });
          }
        });
      documentSurveyList.sort((a, b) => a.completedDate.localeCompare(b.completedDate));
      setCompletedDumentSurveyList(documentSurveyList);
    }
  }, [props?.questionnaireResponseData, props?.currentQrId]);

  const getClosestConsultTask = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    //filter and sort the consultation tasks that are due today or in the future, and get the closest one
    return consultTaskList
      ?.filter((task) => {
        const due = new Date(`${task.duedate}T00:00:00`);
        return due >= today;
      })
      ?.sort((a, b) => new Date(`${a.duedate}T00:00:00`).getTime() - new Date(`${b.duedate}T00:00:00`).getTime())?.[0];
  };

  useEffect(() => {
    if (props?.question?.linkId === 'PW-RD-CONSULT-11') {
      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      props.currentAnswer
        ? setSelectedServiceProvider(props.currentAnswer?.[0]?.valueString)
        : setSelectedServiceProvider(null);
    } else if (
      props?.question?.linkId === 'PW-RD-CONSULT-14' ||
      props?.question?.linkId === ADDENDUM_FIELD_IDS.author ||
      props?.question?.linkId === ADDENDUM_FIELD_IDS.signature
    ) {
      if (props.currentAnswer) {
        setSelectedServiceProvider(props.currentAnswer?.[0]?.valueString);
      } else {
        setSelectedServiceProvider(null);
        if (practitionerName && rdHcPractitionerList.length > 0) {
          const match = rdHcPractitionerList.find((p) => p.text === practitionerName);
          if (match) {
            props.addAnswer([{ valueString: match.value }], props.i, props.j);
            setSelectedServiceProvider(match.value);
          }
        }
      }
    } else if (props?.question?.linkId === 'PW-OTHER-13') {
      if (props.currentAnswer) {
        setSelectedConsultTask(props.currentAnswer?.[0]?.valueString);
      } else {
        const cloestConsultTaskId = getClosestConsultTask()?.key;
        if (cloestConsultTaskId) {
          setSelectedConsultTask(cloestConsultTaskId);
          props.addAnswer([{ valueString: cloestConsultTaskId }], props.i, props.j);
        }
      }
    } else if (props?.question?.linkId === 'PW-PROGRAM-INFO-25a') {
      //if there is existing answer, set the qrId
      //if there is no existing answer, leave it blank
      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      props.currentAnswer
        ? setSelectedCompletedDumentSurvey(props.currentAnswer?.[0]?.valueString)
        : setSelectedCompletedDumentSurvey(null);
    } else if (ALL_COSIGN_PRACTITIONER_IDS.includes(props.question?.linkId as string)) {
      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      props.currentAnswer
        ? setSelectedCosignPractitioner(props.currentAnswer?.[0]?.valueString)
        : setSelectedCosignPractitioner(null);
    } else if (props?.question?.linkId === 'PW-GROUP-CONSULT-TOPIC') {
      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      props.currentAnswer
        ? setSelectedGroupConsultTopic(props.currentAnswer?.[0]?.valueString)
        : setSelectedGroupConsultTopic(null);
    }
  }, [
    props?.question?.linkId,
    props.currentAnswer,
    consultTaskList,
    practitionerName,
    props.answersStateReady,
    rdHcPractitionerList,
  ]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!e.target.value) {
      props.addAnswer(null, props.i, props.j);
    } else {
      props.addAnswer([{ valueString: e.target.value }], props.i, props.j);
    }
  };

  /**
   * The parameter is Semantic's own `onChange` signature rather than that type
   * intersected with `{ target: HTMLElement }`. The intersection asserted something
   * Semantic does not promise — `SyntheticEvent.target` is an `EventTarget` — and
   * because the declared type then did not MATCH `DropdownProps['onChange']`, all six
   * `onChange={handleChangeDropdown}` attachments were rejected.
   *
   * The `referralId` branch genuinely needs `textContent`: it stores the option's
   * visible label, not its `value`. `EventTarget` does not declare `textContent`, so
   * the read goes through a local alias that states what a Semantic dropdown item
   * click actually targets — the rendered `<div class="item">`. The alias is a type
   * assertion rather than an `instanceof` narrowing precisely so the emitted read is
   * the same unguarded property access as before: an `instanceof` guard would return
   * `null` where the current code returns `undefined` for a non-Element target.
   */
  const handleChangeDropdown = (e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    // `DropdownProps['value']` is Semantic's whole union, narrowed once here rather than
    // at each read. A cast, not a runtime check: a non-string `value` must keep reaching
    // the consumers below exactly as it does today.
    const { name, value } = data as { name?: string; value?: string };
    if (name === 'referralId') {
      const label = (e.target as HTMLElement).textContent;
      props.addAnswer([{ valueString: label }], props.i, props.j);

      setSelectedReferralId(label);
    } else if (name === 'serviceProvider') {
      props.addAnswer([{ valueString: value }], props.i, props.j);

      setSelectedServiceProvider(value);
    } else if (name === 'consultTask') {
      const selectedTaskId = value; // ID for backend
      setSelectedConsultTask(selectedTaskId);
      props.addAnswer([{ valueString: value }], props.i, props.j);
    } else if (name === 'documentSurvey') {
      const selectedSurveyId = value; // ID for backend
      setSelectedCompletedDumentSurvey(selectedSurveyId);
      props.addAnswer([{ valueString: value }], props.i, props.j);
    } else if (name === 'cosignPractitioner') {
      setSelectedCosignPractitioner(value);
      props.addAnswer([{ valueString: value }], props.i, props.j);
    } else if (name === 'groupConsultTopic') {
      setSelectedGroupConsultTopic(value);
      props.addAnswer([{ valueString: value }], props.i, props.j);
    }
  };

  //only display active in-progress referrals
  useEffect(() => {
    const filteredReferral =
      props?.allReferralData?.length !== undefined && props.allReferralData.length > 0
        ? props.allReferralData
            .filter((referral) => {
              return referral.status === 'IN-PROGRESS';
            })
            .map((k) => {
              // Optional chains restored to upstream parity. Upstream at `41ee0e63` spells this
              // `{ key: k?.id, value: k?.referralId, text: k?.referralId?.toUpperCase() }` — the
              // port had dropped all three and substituted `!` on the last, which erases to
              // nothing. A referral carrying no `referralId` yields `undefined` upstream and threw
              // a TypeError here, blanking the survey form. The comment that stood here claimed the
              // throw was pre-existing; it was not.
              // Cast, not filtered or defaulted: an option built from a referral missing
              // `referralId` must keep reaching the dropdown as it does today.
              return { key: k?.id, value: k?.referralId, text: k?.referralId?.toUpperCase() } as Option;
            })
        : [];
    setActiveReferralList(filteredReferral);
  }, [props?.allReferralData]);

  useEffect(() => {
    if (props.currentAnswer?.[0]?.valueString) {
      setSelectedReferralId(props.currentAnswer?.[0]?.valueString);
    }
  }, [props.question, props.currentAnswer]);

  //handle special handling for displaying the textbox/referral id dropdown
  const handleDisplay = (linkId: string | undefined, answer: QuestionnaireResponseItemAnswer[] | null | undefined) => {
    //limit the referral answer to 25 characters and show smaller textbox for BCSC-MARE-PD program
    if (linkId === 'PW-REFERRAL-01') {
      if (answer) {
        return (
          <Form>
            <Input
              maxLength="25"
              defaultValue={props.currentAnswer?.[0]?.valueString}
              fluid
              placeholder="Enter Answer Here"
              onChange={handleInput}
              style={{ width: '50%' }}
            />
          </Form>
        );
      } else {
        return (
          <Form>
            <Input
              maxLength="25"
              style={{ width: '50%' }}
              fluid
              placeholder="Enter Answer Here"
              onChange={handleInput}
            />
            {/* limit the referral answer to 25 characters and show smaller textbox for BCSC-MARE-PD program */}
          </Form>
        );
      }
    } else if (linkId === 'PW-PROGRAM-INFO-03') {
      //show referral id dropdown
      return (
        <Dropdown
          basic
          labeled
          selection
          placeholder="Edit Referral ID"
          autoComplete="off"
          selectOnBlur={false}
          name="referralId"
          options={activeReferralList}
          value={asDropdownValue(selectedReferralId)}
          onChange={handleChangeDropdown}
        ></Dropdown>
      );
    } else if (
      linkId === 'PW-RD-CONSULT-11' ||
      linkId === 'PW-RD-CONSULT-14' ||
      linkId === ADDENDUM_FIELD_IDS.author ||
      linkId === ADDENDUM_FIELD_IDS.signature
    ) {
      return (
        <Dropdown
          basic
          labeled
          selection
          search
          placeholder="Select Service Provider"
          autoComplete="off"
          selectOnBlur={false}
          name="serviceProvider"
          options={rdHcPractitionerList}
          value={asDropdownValue(selectedServiceProvider)}
          onChange={handleChangeDropdown}
        ></Dropdown>
      );
    } else if (biggerTextboxLinkIds.includes(linkId as string)) {
      //show bigger textbox
      if (answer) {
        return (
          <Form>
            <TextArea
              defaultValue={props.currentAnswer?.[0]?.valueString}
              fluid
              placeholder="Enter Answer Here"
              onChange={handleInput}
              rows={6}
            />
          </Form>
        );
      } else {
        return (
          <Form>
            <TextArea fluid placeholder="Enter Answer Here" onChange={handleInput} rows={6} />
          </Form>
        );
      }
    } else if (linkId === 'PW-OTHER-13') {
      //show consultation task dropdown
      return (
        <Dropdown
          basic
          labeled
          selection
          placeholder="Edit Consultation Task"
          autoComplete="off"
          selectOnBlur={false}
          name="consultTask"
          options={consultTaskList}
          value={asDropdownValue(selectedConsultTask)}
          onChange={handleChangeDropdown}
        ></Dropdown>
      );
    } else if (linkId === 'PW-GROUP-CONSULT-TOPIC') {
      //show group consult topic dropdown
      return (
        <Dropdown
          basic
          labeled
          selection
          search
          placeholder="Select Group Consult Topic"
          autoComplete="off"
          selectOnBlur={false}
          name="groupConsultTopic"
          options={CONSULT_TOPIC_OPTIONS}
          value={asDropdownValue(selectedGroupConsultTopic)}
          onChange={handleChangeDropdown}
        ></Dropdown>
      );
    } else if (linkId === 'PW-PROGRAM-INFO-25a') {
      //show completed document upload survey dropdown
      return (
        <Dropdown
          basic
          labeled
          selection
          placeholder="Choose Document Upload Survey"
          autoComplete="off"
          selectOnBlur={false}
          name="documentSurvey"
          options={completedDumentSurveyList}
          value={asDropdownValue(selectedCompletedDumentSurvey)}
          onChange={handleChangeDropdown}
        ></Dropdown>
      );
    } else if (ALL_COSIGN_PRACTITIONER_IDS.includes(linkId as string)) {
      return (
        <Dropdown
          basic
          labeled
          selection
          search
          disabled={!!props.disableCosignPractitioner}
          placeholder="Select Co-Signing Practitioner"
          autoComplete="off"
          selectOnBlur={false}
          name="cosignPractitioner"
          options={cosignPractitionerList}
          value={asDropdownValue(selectedCosignPractitioner)}
          onChange={handleChangeDropdown}
        />
      );
    } else {
      //show regular textbox
      if (answer) {
        return (
          <Form>
            <TextArea
              defaultValue={props.currentAnswer?.[0]?.valueString}
              fluid
              placeholder="Enter Answer Here"
              onChange={handleInput}
            />
          </Form>
        );
      } else {
        return (
          <Form>
            <TextArea fluid placeholder="Enter Answer Here" onChange={handleInput} />
          </Form>
        );
      }
    }
  };
  return !props.enable ? (
    <Input value="" disabled fluid placeholder="Enter Answer Here" />
  ) : (
    handleDisplay(props?.question?.linkId, props.currentAnswer)
  );
}

const biggerTextboxLinkIds = ['PW-RD-CONSULT-12', 'PW-RD-CONSULT-16'];
